import { createContext, useContext, useEffect, useMemo, useState } from 'react';

const GeolocationContext = createContext();

export function GeolocationProvider({ children }) {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [isGeolocating, setIsGeolocating] = useState(false);
  const [error, setError] = useState(null);
  const [coordinates, setCoordinates] = useState(null);

  useEffect(() => {
    function attemptGeolocation() {
      if (!navigator.geolocation) {
        setError('Geolocation is not supported by your browser');
        setIsGeolocating(false);
        return;
      }

      setIsGeolocating(true);
      setError(null);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          setCoordinates({ lat, lng });
          setCurrentLocation(`${lat}, ${lng}`);
          setIsGeolocating(false);
        },
        (err) => {
          setError(err.message);
          setCurrentLocation(null);
          setCoordinates(null);
          setIsGeolocating(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // Cache location for 5 minutes
        }
      );
    }

    attemptGeolocation();
  }, []);

  const value = useMemo(
    () => ({
      isGeolocating,
      currentLocation,
      coordinates,
      error,
      isLoaded: true, // Always true now (no external script to load)
    }),
    [isGeolocating, currentLocation, coordinates, error]
  );

  return (
    <GeolocationContext.Provider value={value}>
      {children}
    </GeolocationContext.Provider>
  );
}

export function useGeolocationContext() {
  const context = useContext(GeolocationContext);

  if (context === undefined) {
    console.warn(
      'useGeolocationContext must be used within a GeolocationProvider'
    );
  }

  return context;
}
